'use client'

import { Theme } from '@paddle/paddle-js'
import { useLocale, useTranslations } from 'next-intl'
import { useTheme } from 'next-themes'
import { useEffect, useState, useCallback } from 'react'

import { Button } from '@/components/ui/button'

import { useAuth } from '@/hooks/use-auth'

import { useToast } from '../ui/use-toast'

import { usePaddle } from './checkout-start-context'

interface CheckoutStartProps {
  priceId: string
  isPopular?: boolean
}

export const PAYMENT_PROCESSING_KEY = 'payment_processing_state'

const PAYMENT_VALID_DURATION = 30 * 60 * 1000

const setPaymentProcessing = () => {
  if (typeof window === 'undefined') return

  const paymentInfo = {
    timestamp: new Date().getTime(),
  }
  sessionStorage.setItem(PAYMENT_PROCESSING_KEY, JSON.stringify(paymentInfo))
}

const clearPaymentProcessing = () => {
  if (typeof window === 'undefined') return
  sessionStorage.removeItem(PAYMENT_PROCESSING_KEY)
}

const isPaymentProcessing = (): boolean => {
  if (typeof window === 'undefined') return false

  const paymentData = sessionStorage.getItem(PAYMENT_PROCESSING_KEY)
  if (!paymentData) return false

  try {
    const { timestamp } = JSON.parse(paymentData)
    const now = new Date().getTime()

    return now - timestamp < PAYMENT_VALID_DURATION
  } catch (error) {
    console.error('Error parsing payment data:', error)
    clearPaymentProcessing()
    return false
  }
}

const CheckoutStart = ({ priceId, isPopular }: CheckoutStartProps) => {
  const t = useTranslations('Checkout')
  const currentUrl = typeof window !== 'undefined' ? window.location.origin : ''
  const successUrl = `${currentUrl.replace(/\/$/, '')}/checkout/success?welcome_back=true`
  const theme = useTheme()
  const locale = useLocale()
  const { toast } = useToast()

  const { PaddleState } = usePaddle()
  const paddle = PaddleState.paddle
  const { session } = useAuth()
  const [paymentInProgress, setPaymentInProgress] = useState(false)
  const orgEmail = session?.user?.activeOrganization?.email || ''

  useEffect(() => {
    const checkPaymentStatus = () => {
      setPaymentInProgress(isPaymentProcessing())
    }

    checkPaymentStatus()

    return () => {
      // Clear payment processing state when user leaves the checkout page
      if (typeof window !== 'undefined' && !window.location.href.includes('checkout/success')) {
        clearPaymentProcessing()
      }
    }
  }, [])

  const openCheckout = useCallback(() => {
    // If payment is already in progress, prevent double payment
    if (paymentInProgress) {
      toast({
        description: t('pricingTable.paymentProcessing'),
        variant: 'default',
      })
      return
    }

    setPaymentProcessing()

    // Open the checkout
    paddle?.Checkout.open({
      settings: {
        displayMode: 'overlay',
        theme: (theme?.theme as Theme) ?? 'light',
        locale: locale ?? 'en',
        successUrl: successUrl,
        variant: 'one-page',
      },
      items: [{ priceId: priceId, quantity: 1 }],
      customer: { email: orgEmail },
      customData: {
        email: orgEmail,
        // eslint-disable-next-line camelcase
        tolt_referral: window.tolt_referral,
      },
    })
  }, [paddle, paymentInProgress, priceId, theme, locale, successUrl, orgEmail, t, toast])

  return (
    <div>
      <Button
        className={`w-fit rounded-full border border-black px-4 py-6 text-[18px] font-bold ${isPopular ? 'bg-[#653FFE] text-white' : 'bg-white text-black'}`}
        onClick={() => openCheckout()}
        disabled={paymentInProgress}
      >
        {paymentInProgress ? t('pricingTable.paymentProcessing') : t('pricingTable.select')}
      </Button>
    </div>
  )
}

export default CheckoutStart
